import { useEffect, useMemo } from "react";

import { parse } from "date-fns";

import { TTimeOption } from "@components/TimePicker/types";
import { DATE } from "@utils/constants";

import { InitialOrderDataPart, OrderFields } from "../../constants";
import useOrderData from "../../context/useOrderData";
import { getAvailablePickupTimesFrom, getAvailablePickupTimesTo } from "../../utils/getAvailablePickupTimes";
import getPickupTimeFrameDates from "../../utils/getPickupTimeFrameDates";
import useOrderFormik from "../useOrderFormik";

type TPickupTimeFramesReturn = {
  hasDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isDateAvailable?: (date: Date) => boolean;
  availableTimesFrom?: TTimeOption[];
  availableTimesTo?: TTimeOption[];
};

const usePickupTimeFrames = (isWithTimes: boolean): TPickupTimeFramesReturn => {
  const {
    values: {
      [OrderFields.PICKUP_DATE]: pickupDate,
      [OrderFields.PICKUP_TIME_FROM]: pickupTimeFrom,
      [OrderFields.PICKUP_TIME_TILL]: pickupTimeTo,
    },
    setFieldValue,
  } = useOrderFormik();

  const {
    loadedInitialData,
    dynamicData: { pickupTimeFrames },
  } = useOrderData();

  const { hasDates, minDate, maxDate, isDateAvailable } = useMemo(
    () => getPickupTimeFrameDates(pickupTimeFrames || [], isWithTimes),
    [isWithTimes, pickupTimeFrames]
  );

  const availableTimesFrom = useMemo(
    () => getAvailablePickupTimesFrom(pickupTimeFrames || [], pickupDate || undefined),
    [pickupDate, pickupTimeFrames]
  );
  const availableTimesTo = useMemo(
    () => getAvailablePickupTimesTo(pickupTimeFrames || [], pickupDate || undefined, pickupTimeFrom || undefined),
    [pickupDate, pickupTimeFrames, pickupTimeFrom]
  );

  const isPickupTimeframesLoaded = loadedInitialData[InitialOrderDataPart.PICKUP_TIMEFRAMES];

  // If date is selected and it is no longer available - clear it
  useEffect(() => {
    if (!pickupDate || !isPickupTimeframesLoaded) {
      return;
    }
    const parsedValue = parse(pickupDate, DATE.DATE_TEMPLATE, new Date());
    if (!isDateAvailable?.(parsedValue)) {
      setFieldValue(OrderFields.PICKUP_DATE, "");
      setFieldValue(OrderFields.PICKUP_TIME_FROM, "");
      setFieldValue(OrderFields.PICKUP_TIME_TILL, "");
    }
  }, [isDateAvailable, isPickupTimeframesLoaded, pickupDate, setFieldValue]);

  useEffect(() => {
    if (!pickupTimeFrom || !isPickupTimeframesLoaded) {
      return;
    }
    const selectedTimeFrom = availableTimesFrom?.find(({ time }) => time === pickupTimeFrom);
    if (!selectedTimeFrom) {
      setFieldValue(OrderFields.PICKUP_TIME_FROM, "");
      setFieldValue(OrderFields.PICKUP_TIME_TILL, "");
    }
  }, [availableTimesFrom, isPickupTimeframesLoaded, pickupTimeFrom, setFieldValue]);

  useEffect(() => {
    if (!pickupTimeTo || !isPickupTimeframesLoaded) {
      return;
    }
    const selectedTimeTo = availableTimesTo?.find(({ time }) => time === pickupTimeTo);
    if (!selectedTimeTo) {
      setFieldValue(OrderFields.PICKUP_TIME_TILL, "");
    }
  }, [availableTimesTo, isPickupTimeframesLoaded, pickupTimeTo, setFieldValue]);

  return { hasDates, minDate, maxDate, isDateAvailable, availableTimesFrom, availableTimesTo };
};
export default usePickupTimeFrames;
